import { rest } from 'msw'
import { Profile } from 'types/types'
import { LoginUserResponse } from 'types/users'
import mockUnauthorizedError from '../../../__mocks__/Auth/loginError.json'
import mockLoginUserResponse from '../../../__mocks__/Auth/loginUserResponse.json'
import mockRecaptchaScoreError from '../../../__mocks__/Auth/recaptchaScoreError.json'
import mockProfile from '../../../__mocks__/profile.json'

const API_URL = process.env.API_URL ?? 'https://jestjs.io'

export const postLoginUser = rest.post<LoginUserResponse>(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(ctx.json(mockLoginUserResponse))
  }
)

const getUserProfile = rest.get<Profile>(`${API_URL}/api/user/profile`, async (req, res, ctx) => {
  return res(ctx.json(mockProfile))
})

export const getUserProfileWithOverrides = (
  overrides: Omit<Partial<typeof mockProfile>, 'options'> & {
    options?: Partial<(typeof mockProfile)['options']>
  } = {}
) =>
  rest.get<Profile>(`${API_URL}/api/user/profile`, (_req, res, ctx) => {
    return res(
      ctx.json({
        ...mockProfile,
        ...overrides,
        options: {
          ...mockProfile.options,
          ...(overrides.options ?? {}),
        },
      })
    )
  })

export const unauthorizedHandlerException = rest.post(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(ctx.status(401), ctx.json(mockUnauthorizedError))
  }
)

export const recaptchaLowScoreException = rest.post(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(ctx.status(400), ctx.json(mockRecaptchaScoreError))
  }
)

export const tooManyRequestsException = rest.post(`${API_URL}/api/login`, async (req, res, ctx) => {
  return res(ctx.status(429))
})

export const twoFAIncorrectException = rest.post(`${API_URL}/api/login`, async (req, res, ctx) => {
  return res(ctx.status(412))
})

export const forbiddenException = rest.post(`${API_URL}/api/login`, async (req, res, ctx) => {
  return res(ctx.status(403), ctx.json({ message: 'Forbidden' }))
})

export const twoFactorRequiredException = rest.post(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(ctx.status(406))
  }
)

export const workspaceRequiredException = rest.post(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(
      ctx.status(409),
      ctx.json([
        { id: 123, name: 'Workspace A' },
        { id: 456, name: 'Workspace B' },
      ])
    )
  }
)

export const recaptchaVerificationException = rest.post(
  `${API_URL}/api/login`,
  async (req, res, ctx) => {
    return res(ctx.status(400), ctx.json({ message: 'Recaptcha verification failed' }))
  }
)

export const serverErrorException = rest.post(`${API_URL}/api/login`, async (req, res, ctx) => {
  return res(ctx.status(500), ctx.json({ message: 'Internal server error' }))
})

export const loginSuccessNoRoles = rest.post(`${API_URL}/api/login`, async (req, res, ctx) => {
  return res(ctx.json({ ...mockLoginUserResponse, roles: [] }))
})

export const reissueToken = rest.post(`${API_URL}/api/login/reissue`, async (req, res, ctx) => {
  return res(
    ctx.status(200),
    ctx.json({
      username: 'user_name',
      roles: ['ROLE_USER'],
      token_type: 'Hawk',
      access_token: 'access_token',
      secret: 'secret',
    })
  )
})

export const postResetPasswordSuccess = rest.post(
  `${API_URL}/api/user/forgot/confirm`,
  async (_req, res, ctx) => res(ctx.status(200), ctx.json({}))
)

export const postResetPasswordGenericError = rest.post(
  `${API_URL}/api/user/forgot/confirm`,
  async (_req, res, ctx) => res(ctx.status(500), ctx.json({}))
)

export const postResetPasswordComplexityError = rest.post(
  `${API_URL}/api/user/forgot/confirm`,
  async (_req, res, ctx) =>
    res(
      ctx.status(412),
      ctx.json({
        errorList: [
          {
            parameter: 'password',
            code: 'complexity',
            message: 'Password does not meet complexity requirements',
          },
        ],
      })
    )
)

export const postResetPassword412NoMatch = rest.post(
  `${API_URL}/api/user/forgot/confirm`,
  async (_req, res, ctx) =>
    res(
      ctx.status(412),
      ctx.json({
        errorList: [{ parameter: 'other', code: 'other', message: 'Unrelated error' }],
      })
    )
)

export default [
  postLoginUser,
  getUserProfile,
  unauthorizedHandlerException,
  twoFAIncorrectException,
  tooManyRequestsException,
  reissueToken,
]
